/**
 * Focus selection: what the player is looking at and close enough to touch.
 *
 * Walking over a pickup does nothing, deliberately. If items jumped into the
 * bag on contact then exploring would stop being an act and become a side
 * effect of moving, and looking at the room is most of what this game is.
 */

import { hasLineOfSight } from '../core/grid';
import { clamp01, lerp } from '../core/math';
import type { Grid, Interactable, PlayerState } from '../core/types';

/** Cosine of the accepted half-angle at touching distance and at full range. */
const NEAR_DOT = 0.5;
const FAR_DOT = 0.86;
/** Inside this the direction to the target is meaningless, so take it on trust. */
const POINT_BLANK = 0.6;
/** Height a pickup sits at, for the vertical aim test. */
const ITEM_HEIGHT = 0.35;
/** How far off the true vertical angle the player may look, radians. */
const PITCH_TOLERANCE = 1.15;
/** Metres of preference given to a closer target when two are both in the cone. */
const DISTANCE_BIAS = 0.06;

/**
 * Picks the best interactable under the crosshair. Holds no per-frame garbage:
 * one instance lives for the life of the survival system.
 */
export class FocusFinder {
  private best: Interactable | null = null;
  private bestScore = 0;
  private fx = 0;
  private fz = 0;

  /**
   * Two pools rather than one concatenated list so the caller never has to
   * allocate a joined array every frame.
   */
  find(
    player: PlayerState,
    grid: Grid,
    maxRange: number,
    primary: readonly Interactable[],
    secondary: readonly Interactable[],
  ): Interactable | null {
    this.best = null;
    this.bestScore = -Infinity;
    this.fx = Math.sin(player.yaw);
    this.fz = -Math.cos(player.yaw);
    this.consider(player, grid, maxRange, primary);
    this.consider(player, grid, maxRange, secondary);
    return this.best;
  }

  private consider(player: PlayerState, grid: Grid, maxRange: number, pool: readonly Interactable[]): void {
    for (const it of pool) {
      const dx = it.pos.x - player.pos.x;
      const dz = it.pos.z - player.pos.z;
      const d2 = dx * dx + dz * dz;
      const reach = Math.min(maxRange, it.radius);
      if (d2 > reach * reach) continue;

      const d = Math.sqrt(d2);
      let score: number;
      if (d < POINT_BLANK) {
        score = 1;
      } else {
        const dot = (dx * this.fx + dz * this.fz) / d;
        if (dot < lerp(NEAR_DOT, FAR_DOT, clamp01(d / maxRange))) continue;
        // Vertical aim: pickups are on the floor, so looking at the ceiling
        // should not pick them up, but a normal forward glance should.
        const wanted = Math.atan2(ITEM_HEIGHT - player.eyeHeight, d);
        if (Math.abs(player.pitch - wanted) > PITCH_TOLERANCE) continue;
        score = dot;
      }
      score -= d * DISTANCE_BIAS;
      if (score <= this.bestScore) continue;
      if (!hasLineOfSight(grid, player.pos, it.pos)) continue;
      this.best = it;
      this.bestScore = score;
    }
  }
}
